import { useState, type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useOutletContext } from "react-router-dom";
import moment, { type Moment } from "moment-timezone";

import { type IncidentReport, type IncidentReportReferringManagerContact } from "components/CriticalIncident/interfaces";
import { useThrottledSectionUpdate } from "../../hooks/useThrottledSectionUpdate";
import { FormControlWrapper } from "components/Shared";
import { TextBox, DateInput, Button } from "components/Shared/Inputs";
import { confirm } from "components/Shared/ConfirmDialog";
import { ReportSectionAccordion } from "components/CriticalIncident/components/Shared/ReportSectionAccordion";
import { ChooseYesNo } from "components/CriticalIncident/components/ChooseYesNo";

import "./ReferringManager.scss";

const MAX_CHARS = 2000;
const MAX_CONTACTS = 10;

const EMPTY_CONTACT: IncidentReportReferringManagerContact = {
  date: null,
  details: '',
};

export const ReferringManager: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { values, handleChange, touched, errors, setFieldValue } = useFormikContext<IncidentReport>();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const throttledUpdateReport = useThrottledSectionUpdate('referringManager', values.referringManager);
  const [expandedContactIndex, setExpandedContactIndex] = useState<number | false>(-1);

  const handleGateChange = async (wasThereContactWithReferringManager: boolean) => {
    if (!wasThereContactWithReferringManager && values.referringManager?.wasThereContactWithReferringManager) {
      const confirmed = await confirm({
        title: t('clear_referring_manager_contacts_confirm_title'),
        message: t('clear_referring_manager_contacts_confirm_text'),
        confirmLabel: t('yes_remove'),
      });
      if (!confirmed) return;
    }

    const hadContacts = Boolean(values.referringManager?.contacts?.length);
    const contacts = wasThereContactWithReferringManager
      ? (hadContacts ? values.referringManager?.contacts : [{ ...EMPTY_CONTACT }])
      : [];

    setFieldValue('referringManager.wasThereContactWithReferringManager', wasThereContactWithReferringManager);
    setFieldValue('referringManager.contacts', contacts);
    throttledUpdateReport({ wasThereContactWithReferringManager, contacts });
    setExpandedContactIndex(wasThereContactWithReferringManager && !hadContacts ? 0 : false);
  };

  const handleAddContact = () => {
    const contacts = values.referringManager?.contacts ?? [];
    if (contacts.length >= MAX_CONTACTS) return;

    const nextContacts = [...contacts, { ...EMPTY_CONTACT }];
    setFieldValue('referringManager.contacts', nextContacts);
    throttledUpdateReport({ contacts: nextContacts });
    setExpandedContactIndex(nextContacts.length - 1);
  };

  const handleRemoveContact = (index: number) => {
    const contacts = values.referringManager?.contacts.filter((_, i) => i !== index);
    setFieldValue('referringManager.contacts', contacts);
    throttledUpdateReport({ contacts });
  };

  const updateContactField = (index: number, field: keyof IncidentReportReferringManagerContact, value: unknown) => {
    const contacts = values.referringManager?.contacts.map((contact, i) => i === index ? { ...contact, [field]: value } : contact);
    throttledUpdateReport({ contacts });
  };

  const makeContactChangeHandler = (index: number, field: keyof IncidentReportReferringManagerContact) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleChange(event);
      updateContactField(index, field, event.target.value);
    };

  const handleContactDateChange = (index: number) => (value: Moment | null) => {
    const isoString = value?.toDate().toISOString();
    setFieldValue(`referringManager.contacts[${index}].date`, isoString);
    updateContactField(index, 'date', isoString);
  };

  return translationsReady && (
    <div className="referring-manager">
      <h2>{t('referringManager')}</h2>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="radio.yesno"
            label={`${t('was_there_contact_with_referring_manager')}*`}
            name="referringManager.wasThereContactWithReferringManager"
            fullWidth
            error={touched.referringManager?.wasThereContactWithReferringManager && Boolean(errors?.referringManager?.wasThereContactWithReferringManager)}
            helperText={touched.referringManager?.wasThereContactWithReferringManager ? t(errors?.referringManager?.wasThereContactWithReferringManager || ' ') : ''}
          >
            <ChooseYesNo
              label={`${t('was_there_contact_with_referring_manager')}*`}
              name="referringManager.wasThereContactWithReferringManager"
              value={values.referringManager?.wasThereContactWithReferringManager}
              onChange={(value) => handleGateChange(value as boolean)}
            />
          </FormControlWrapper>
        </Grid>

        {values.referringManager?.wasThereContactWithReferringManager && (
          <>
            {values.referringManager?.contacts?.map((contact, index) => {
              const contactTouched = touched.referringManager?.contacts?.[index] as Record<string, unknown> | undefined;
              const contactErrors = errors?.referringManager?.contacts?.[index] as Record<string, string> | undefined;

              return (
                <Grid item sm={12} key={index}>
                  <ReportSectionAccordion
                    label={t('referring_manager_contact_number', { number: index + 1 })}
                    name={`referringManager.contacts[${index}]`}
                    pristineValue={EMPTY_CONTACT}
                    removeAction={isReadonly ? undefined : () => handleRemoveContact(index)}
                    removeHidden={values.referringManager?.contacts.length <= 1}
                    expanded={expandedContactIndex === index}
                    onToggle={(isExpanded) => setExpandedContactIndex(isExpanded ? index : false)}
                    readonly={isReadonly}
                  >
                    <Grid container spacing={2}>
                      <Grid item sm={6}>
                        <FormControlWrapper
                          isReadonly={isReadonly}
                          type="date"
                          dateFormat="L"
                          label={t('date')}
                          name={`referringManager.contacts[${index}].date`}
                        >
                          <DateInput
                            label={t('date')}
                            required
                            name={`referringManager.contacts[${index}].date`}
                            onChange={handleContactDateChange(index)}
                            value={contact.date ? moment(contact.date) : null}
                            error={Boolean(contactTouched?.date) && Boolean(contactErrors?.date)}
                            helperText={contactTouched?.date ? t(contactErrors?.date || ' ') : ''}
                            fullWidth
                          />
                        </FormControlWrapper>
                      </Grid>
                      <Grid item sm={12}>
                        <FormControlWrapper
                          isReadonly={isReadonly}
                          label={t('details')}
                          name={`referringManager.contacts[${index}].details`}
                          fullWidth
                        >
                          <TextBox
                            multiline
                            minRows={4}
                            maxRows={6}
                            label={t('details')}
                            required
                            name={`referringManager.contacts[${index}].details`}
                            value={contact.details}
                            onChange={makeContactChangeHandler(index, 'details')}
                            slotProps={{ input: { maxLength: MAX_CHARS } }}
                            error={Boolean(contactTouched?.details) && Boolean(contactErrors?.details)}
                            helperText={contactTouched?.details ? t(contactErrors?.details || ' ') : ''}
                          />
                        </FormControlWrapper>
                      </Grid>
                    </Grid>
                  </ReportSectionAccordion>
                </Grid>
              );
            })}

            {!isReadonly && values.referringManager?.contacts.length < MAX_CONTACTS && (
              <Grid item sm={12}>
                <Button variant="text" className="referring-manager__add-button" onClick={handleAddContact}>
                  <AddIcon fontSize="small" />
                  {t('add_referring_manager_contact')}
                </Button>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </div>
  );
};
